import { useCallback, useEffect, useRef } from 'react';

/**
 * Heavily inspired by https://github.com/xnimorz/use-debounce
 */
export function useDebouncedCallback<P extends unknown[]>(
    callback: (...args: P) => void,
    delay: number,
    options: { maxWait?: number; leading?: boolean; trailing?: boolean } = {}
): [(...args: P) => void, () => void] {
    const maxWait = options.maxWait;
    const maxWaitHandler = useRef<ReturnType<typeof setTimeout> | null>(null);
    const maxWaitArgs = useRef<P | []>([]);

    const leading = options.leading;
    const trailing = options.trailing === undefined ? true : options.trailing;
    const leadingCall = useRef(false);

    const functionTimeoutHandler = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isComponentUnmounted = useRef(false);

    const debouncedFunction = useRef(callback);
    debouncedFunction.current = callback;

    const cancelDebouncedCallback: () => void = useCallback(() => {
        if (functionTimeoutHandler.current) {
            clearTimeout(functionTimeoutHandler.current);
        }
        if (maxWaitHandler.current) {
            clearTimeout(maxWaitHandler.current);
        }
        maxWaitHandler.current = null;
        maxWaitArgs.current = [];
        functionTimeoutHandler.current = null;
        leadingCall.current = false;
    }, []);

    useEffect(
        () => () => {
            // we use flag, as we allow to call callPending outside the hook
            isComponentUnmounted.current = true;
        },
        []
    );

    const debouncedCallback = useCallback(
        (...args: P) => {
            maxWaitArgs.current = args;
            if (functionTimeoutHandler.current) {
                clearTimeout(functionTimeoutHandler.current);
            }
            if (leadingCall.current) {
                leadingCall.current = false;
            }
            if (!functionTimeoutHandler.current && leading && !leadingCall.current) {
                debouncedFunction.current(...args);
                leadingCall.current = true;
            }

            functionTimeoutHandler.current = setTimeout(() => {
                let shouldCallFunction = true;
                if (leading && leadingCall.current) {
                    shouldCallFunction = false;
                }
                cancelDebouncedCallback();

                if (!isComponentUnmounted.current && trailing && shouldCallFunction) {
                    debouncedFunction.current(...args);
                }
            }, delay);

            if (maxWait && !maxWaitHandler.current && trailing) {
                maxWaitHandler.current = setTimeout(() => {
                    const args = maxWaitArgs.current;
                    cancelDebouncedCallback();

                    if (!isComponentUnmounted.current) {
                        debouncedFunction.current.apply(null, args as P);
                    }
                }, maxWait);
            }
        },
        [maxWait, delay, cancelDebouncedCallback, leading, trailing]
    );

    return [debouncedCallback, cancelDebouncedCallback];
}
